import { Color, Spacing, Typography } from '@persona-id/design-tokens';
import { ChangeEventValue, Coords } from 'google-map-react';
import { capitalize, keyBy } from 'lodash/fp';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useState } from 'react';
import { Breakpoint, MEDIA_WIDTHS, mediaQuery } from 'shared/styles/variables';
import styled from 'styled-components';

import Map, { MapMarker } from 'dashboard/components/Map';
import { stringifyLatitude, stringifyLongitude } from 'dashboard/lib/utils/stringFormat';
import { Device } from 'dashboard/models/device';
import { InquirySession } from 'dashboard/models/inquiry-session';
import Attribute, { AttributeGrid } from 'design-system/components/Attribute';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import ToggleSlider from 'design-system/components/ToggleSlider';
import useSet from 'lib/hooks/useSet';
import { Place } from 'lib/interfaces/place';
import { computeInitialMapCenterAndZoom, serializePlace } from 'lib/utils/maps';

// Summary data gets scrunched up on smaller screen widths even at fullHD width
const WIDESCREEN_BP = (MEDIA_WIDTHS.fullHD + 200) as Breakpoint;

const ColumnWrapper = styled.div`
  display: flex;
  flex-direction: column-reverse;
  width: 100%;

  // Move map to side when screen is wide enough
  @media ${mediaQuery({ min: WIDESCREEN_BP })} {
    flex-direction: row;
  }
`;
const LeftColumn = styled.div`
  display: block;
  width: 100%;

  h6 {
    display: none;
  }

  // Move map to side when screen is wide enough
  @media ${mediaQuery({ min: WIDESCREEN_BP })} {
    width: 50%;
    margin-right: ${Spacing[3]};

    h6 {
      display: block;
    }
  }
`;
const RightColumn = styled.div`
  display: block;
  width: 100%;
  margin-bottom: ${Spacing[4]};

  // Move map to side when screen is wide enough
  @media ${mediaQuery({ min: WIDESCREEN_BP })} {
    width: 50%;
    margin-left: ${Spacing[3]};
    margin-left: 0;

    h6 {
      display: none;
    }
  }
`;
const FullColumn = styled.div`
  display: block;
  width: 100%;
`;
const MarkerLink = styled.a`
  &:hover {
    cursor: pointer;
  }
`;
const SessionDetailsWrapper = styled.span`
  color: ${Color.CoreSlate800};
`;
// NOTE: used to group sessions without device data
const UNKNOWN_DEVICE_TOKEN = 'UnknownDevice';
const UNKNOWN_DEVICE_NAME = 'Unknown device';

const MAP_HEIGHT = 300;
const MIN_MAP_WIDTH = 400;
const CLICK_ZOOM = 7;
const MapContainer = styled('div')`
  flex: none;
  height: ${MAP_HEIGHT}px;
`;
const MarkerHeader = styled.h4`
  ${Typography.TextMediumBold}
`;
const MarkerDetails = styled.p`
  ${Typography.TextSmall}
`;
const DeviceDetailsWrapper = styled.span`
  color: ${Color.CoreSlate800};
`;
const CreatedAtWrapper = styled.span`
  color: ${Color.CoreSlate800};
  font-weight: 700;
`;

export type InquiryDeviceSessionSummaryItem = SummaryItemWithLocation | SummaryItemWithoutLocation;

interface SummaryItemWithoutLocation {
  deviceDetails: string;
  deviceToken: string;
  sessionName: string;
  sessionCreatedAt: string;
  formattedLatLng: string | null | undefined;
  latitude: number | null | undefined;
  longitude: number | null | undefined;
  formattedGpsLatLng: string | null | undefined;
  gpsLatitude: number | null | undefined;
  gpsLongitude: number | null | undefined;
}

interface SummaryItemWithLocation extends SummaryItemWithoutLocation {
  formattedLatLng: string;
  latitude: number;
  longitude: number;
}

interface InquiryDeviceSessionSummaryProps {
  inquirySessions: InquirySession[];
  devices: Device[];
  isRedacted: boolean;
  onlyDisplayStartedSessions: boolean;
  toggleOnlyDisplayStartedSessions: () => void;
}

const InquiryDeviceSessionSummary = ({
  inquirySessions,
  devices,
  isRedacted,
  onlyDisplayStartedSessions,
  toggleOnlyDisplayStartedSessions,
}: InquiryDeviceSessionSummaryProps) => {
  const formatLatLong = useCallback((latitude?: number, longitude?: number): string | null => {
    if (latitude == null || longitude == null) {
      return null;
    }
    return `${stringifyLatitude(latitude)} ${stringifyLongitude(longitude)}`;
  }, []);

  const { deviceSessionFullList, groupedDeviceSessions } = useMemo(() => {
    const deviceSessionFullList: InquiryDeviceSessionSummaryItem[] = [];
    const groupedDeviceSessions: {
      [key: string]: { name: string; items: InquiryDeviceSessionSummaryItem[] };
    } = {};

    inquirySessions.forEach((session: InquirySession, idx: number) => {
      const device = keyBy('id', devices)[session.relationships.device.data?.id];
      const deviceToken = session.relationships.device.data?.id ?? '';
      const deviceDetails = device
        ? summaryNameHelper(device, UNKNOWN_DEVICE_NAME)
        : UNKNOWN_DEVICE_NAME;
      const formattedLatLng = isRedacted
        ? null
        : formatLatLong(session.attributes.latitude, session.attributes.longitude);

      const formattedGpsLatLng = isRedacted
        ? null
        : formatLatLong(session.attributes['gps-latitude'], session.attributes['gps-longitude']);

      const item: InquiryDeviceSessionSummaryItem = {
        deviceDetails: deviceDetails,
        deviceToken: deviceToken,
        sessionName:
          session.attributes['started-at'] != null
            ? `Session ${idx + 1}`
            : `Session ${idx + 1} (Not started)`,
        sessionCreatedAt: DateTime.fromISO(session.attributes['created-at']).toFormat(
          'MMM d, yyyy h:mm a ',
        ),
        formattedLatLng: formattedLatLng,
        latitude: session.attributes.latitude,
        longitude: session.attributes.longitude,
        formattedGpsLatLng: formattedGpsLatLng,
        gpsLatitude: session.attributes['gps-latitude'],
        gpsLongitude: session.attributes['gps-longitude'],
      };

      if (device && deviceToken) {
        if (groupedDeviceSessions[deviceToken]) {
          groupedDeviceSessions[deviceToken].items.push(item);
        } else {
          groupedDeviceSessions[deviceToken] = {
            name: `${item.deviceDetails}`,
            items: [item],
          };
        }
      } else {
        if (groupedDeviceSessions[UNKNOWN_DEVICE_TOKEN]) {
          groupedDeviceSessions[UNKNOWN_DEVICE_TOKEN].items.push(item);
        } else {
          groupedDeviceSessions[UNKNOWN_DEVICE_TOKEN] = {
            name: UNKNOWN_DEVICE_NAME,
            items: [item],
          };
        }
      }

      /**
       * NOTE: only need deviceSessionFullList for the map if we have a valid lat/lng
       * and we are allowed to show them
       */
      if (!isRedacted && (formattedLatLng || formattedGpsLatLng)) {
        deviceSessionFullList.push(item);
      }
    });

    return { deviceSessionFullList, groupedDeviceSessions };
  }, [devices, inquirySessions, isRedacted]);

  const { places, markers } = useMemo(() => {
    const markers: MapMarker[] = [];
    const places: Place[] = [];

    for (const item of deviceSessionFullList) {
      if (item.latitude != null && item.longitude != null) {
        const existingMarker = markers.find(
          (m) => item.latitude === m.lat && item.longitude === m.lng,
        );

        if (existingMarker) {
          existingMarker.overlapCount ??= 0;
          existingMarker.overlapCount++;
          if (existingMarker.secondaryTooltipLabel) {
            existingMarker.secondaryTooltipLabel = MapMarker.createUpdatedMarkerTooltip(
              existingMarker.secondaryTooltipLabel,
              'IP',
            );
          }
        } else {
          const networkMarker = MapMarker.fromInquiryDeviceSessionSummaryItem(item);
          if (networkMarker) {
            markers.push(networkMarker);
            places.push({ lat: networkMarker.lat, lng: networkMarker.lng });
          }
        }
      }
      if (item.gpsLatitude != null && item.gpsLongitude != null) {
        const existingMarker = markers.find(
          (m) => item.gpsLatitude === m.lat && item.gpsLongitude === m.lng,
        );
        if (existingMarker) {
          existingMarker.overlapCount ??= 0;
          existingMarker.overlapCount++;
          if (existingMarker.secondaryTooltipLabel) {
            existingMarker.secondaryTooltipLabel = MapMarker.createUpdatedMarkerTooltip(
              existingMarker.secondaryTooltipLabel,
              'GPS',
            );
          }
        } else {
          const gpsMarker = MapMarker.fromInquiryDeviceSessionSummaryItem(item, { useGps: true });
          if (gpsMarker) {
            markers.push(gpsMarker);
            places.push({ lat: gpsMarker.lat, lng: gpsMarker.lng });
          }
        }
      }
    }

    return { places, markers };
  }, [deviceSessionFullList]);

  const { center: initialCenter, zoom: initialZoom } = useMemo(
    () => computeInitialMapCenterAndZoom(MAP_HEIGHT, places, MIN_MAP_WIDTH),
    [places],
  );

  /**
   * Tracks when markers are "active", e.g., when they are hovered either in
   * the map or in summary list in the left column of the card.
   */
  const { add: activateMarker, delete: deactivateMarker, has: isMarkerActive } = useSet<string>();

  const [center, updateCenter] = useState<Coords>(initialCenter);
  const [zoom, updateZoom] = useState<number>(initialZoom);

  const isMapMarkerActive = useCallback(
    (place: Place) => isMarkerActive(serializePlace(place)),
    [isMarkerActive],
  );

  const renderMarkerTooltip = useCallback(
    (marker: MapMarker) => {
      const items = deviceSessionFullList.filter(
        (item) =>
          item.formattedLatLng === marker.primaryTooltipLabel ||
          item.formattedGpsLatLng === marker.primaryTooltipLabel,
      );

      return <MarkerTooltip marker={marker} summaryItems={items}></MarkerTooltip>;
    },
    [deviceSessionFullList],
  );

  const handleMapChange = useCallback(({ center: { lat, lng }, zoom }: ChangeEventValue) => {
    updateCenter({ lat, lng });
    updateZoom(zoom);
  }, []);

  const handleMarkerClick = useCallback(
    (_markerIdx: number, { lat, lng }: Coords) => updateCenter({ lat, lng }),
    [],
  );

  const handleLocationDetailClick = useCallback((place: Place) => {
    updateCenter({ lat: place.lat, lng: place.lng });
    updateZoom(CLICK_ZOOM);
  }, []);

  // NOTE: toggles tooltip when hovering on details in the Summary in left column
  const handleLocationDetailMouseOver = useCallback(
    (place: Place) => activateMarker(serializePlace(place)),
    [activateMarker],
  );

  const handleLocationDetailMouseOut = useCallback(
    (place: Place) => deactivateMarker(serializePlace(place)),
    [deactivateMarker],
  );

  const sessionDeviceList = Object.keys(groupedDeviceSessions).map((token, idx) => {
    return (
      <section key={`summary_section_${idx}`}>
        <Card.SectionHeader
          subtitle={
            token === UNKNOWN_DEVICE_TOKEN ? (
              ''
            ) : (
              <SessionDetailsWrapper>{`(${token})`}</SessionDetailsWrapper>
            )
          }
        >
          {groupedDeviceSessions[token].name}
        </Card.SectionHeader>
        <AttributeGrid horizontal='full-width'>
          {groupedDeviceSessions[token].items.map((item, idx) => {
            const itemAsPlace: Place | null =
              item.latitude == null || item.longitude == null || isRedacted
                ? null
                : { lat: item.latitude, lng: item.longitude };

            return (
              <Attribute
                key={`session_summary_${idx}`}
                label={item.sessionName}
                value={
                  itemAsPlace == null ? (
                    <SessionDetailsWrapper>
                      {item.sessionCreatedAt}
                      <br />
                      (location {isRedacted ? 'redacted' : 'unavailable'})
                    </SessionDetailsWrapper>
                  ) : (
                    <MarkerLink
                      onClick={() => handleLocationDetailClick(itemAsPlace)}
                      onMouseOver={() => handleLocationDetailMouseOver(itemAsPlace)}
                      onMouseOut={() => handleLocationDetailMouseOut(itemAsPlace)}
                    >
                      {item.sessionCreatedAt}
                    </MarkerLink>
                  )
                }
              />
            );
          })}
        </AttributeGrid>
        <Spacer height={3} />
      </section>
    );
  });

  return (
    <>
      <Card.Title
        actions={
          <ToggleSlider
            value={onlyDisplayStartedSessions}
            onChange={toggleOnlyDisplayStartedSessions}
            labelOn='Only show started sessions'
            labelOff='Only show started sessions'
          />
        }
      >
        Summary
      </Card.Title>
      <ColumnWrapper>
        {isRedacted || deviceSessionFullList.length === 0 ? (
          <FullColumn>{sessionDeviceList}</FullColumn>
        ) : (
          <>
            <LeftColumn>{sessionDeviceList}</LeftColumn>
            <RightColumn>
              <MapContainer>
                <Map
                  markers={markers}
                  center={center}
                  zoom={zoom}
                  onChange={handleMapChange}
                  onMarkerClick={handleMarkerClick}
                  isMarkerActive={isMapMarkerActive}
                  renderTooltip={renderMarkerTooltip}
                />
              </MapContainer>
            </RightColumn>
          </>
        )}
      </ColumnWrapper>
    </>
  );
};

interface MarkerTooltipProps {
  summaryItems: InquiryDeviceSessionSummaryItem[];
  marker: MapMarker;
}

const MarkerTooltip = ({ summaryItems, marker }: MarkerTooltipProps) => {
  return (
    <>
      {marker.primaryTooltipLabel && <MarkerHeader>{marker.primaryTooltipLabel}</MarkerHeader>}
      {summaryItems &&
        summaryItems.map((item, idx) => {
          return (
            <MarkerDetails key={`${idx}_device_marker`}>
              <CreatedAtWrapper>{item.sessionCreatedAt}</CreatedAtWrapper>
              <br />
              <DeviceDetailsWrapper>{marker.secondaryTooltipLabel}</DeviceDetailsWrapper>
            </MarkerDetails>
          );
        })}
    </>
  );
};

export default InquiryDeviceSessionSummary;

// NOTE: casing and whatnot make it difficult to create the correct format if a data point is missing
// only exporting so it can be unit tested
export function summaryNameHelper(device: Device, fallbackName: string): string {
  const {
    'browser-name': browserName,
    'device-type': deviceType,
    'os-name': osName,
  } = device?.attributes || {};

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const nameAndDevice = [osName, osName == null ? capitalize(deviceType) : deviceType]
    .filter(Boolean)
    .join(' ');
  const allParts = [nameAndDevice, browserName].filter(Boolean);

  return allParts.length > 0 ? allParts.join(', ') : fallbackName;
}
